import { FromPrice, PriceTypes } from './components/Price';
import { AspectRatio } from '~/shared/components/AspectRatio/AspectRatio';
import { Image } from '~/shared/components/Image/Image';
import { useCallback, useMemo, useState } from 'react';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { ProductLabelSetting, mq } from '~/lib';
import { TranslationKey } from '~/lib/data-contract/auto-generated/translation-model';
import { EventIdentifier, useEvents } from '~/shared/hooks/useEvents';
import { _transformProductDetailsToDataLayer, getListName } from '~/shared/hooks/useEvents/helpers';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import Flex from '../Flex/Flex';
import { TProductDetails } from './ProductCard.definition';
import { CTA } from './components/CTA';
import { Favourite } from './components/Favourite';
import { Notifications } from './components/Notifications';
import { Prices } from './components/Price';
import { ProductCardLink } from './components/ProductCardLink';
import { Rating } from './components/Rating';
import { Splash } from './components/Splash';

import { TopInfo } from './components/TopInfo';
import { toBoolean } from '~/shared/utils/toBoolean';
import { ImageProps as NextImageProps } from 'next/image';
import { Swatches } from './components/swatches/Swatches';
import { useProductLabels } from '~/shared/hooks/useProductLabels';
import styles from './ProductCard.module.scss';

import { useBasket } from '~/features/basket/hooks';
import { usePage } from '~/templates/pages';
import { GTM_LIST_ID } from '~/shared/hooks/useEvents/useGTMEvents/models';

/**
 * Other components are relying on this `extra` type declariation.
 * This should probably just be renamed in the files importing it.
 */
export type ProductCardProps = TProductDetails & {
    beforePrice?: number;
    imageLoading?: NextImageProps['loading'];
};

export const ProductCard = (
    product: TProductDetails & {
        listId: EventIdentifier;
        listName?: EventIdentifier;
        position: number;
        raptorAlgorithm?: string;
        imageLoading?: NextImageProps['loading'];
        _rankingInfo?: { promoted: boolean };
    },
) => {
    const {
        name,
        longName,
        label,
        brand,
        splash,
        images,
        imageLoading,
        priority,
        notifications,
        prices,
        id,
        url,
        onBuyBtn,
        onFavourite,
        cust_group,
        cust_in_stock,
        short_description,
        cust_swatchimages_siblings,
        listId,
        listName,
        position,
        isFavourite,
        raptorAlgorithm,
        ctaButtonProductCard,
        cust_minimumPrice,
    } = product;

    // Disable buy button until basket is ready for the customer
    const { isInitialized } = useBasket();

    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { breadcrumb } = usePage();
    const { productClickEvent, setList } = useEvents(frame);

    const { labelsTopLeft, labelsTopRight } = useProductLabels(product);
    const firstImage = (images && images[0]) ?? null;
    const { isPlusMember } = useCustomer();
    const [isDisabled, setIsDisabled] = useState(false);

    const isGiftCard = !!cust_group?.includes('giftcard');

    const productEventData = useMemo(
        () => _transformProductDetailsToDataLayer(product, 1, position, isPlusMember),
        [product, position, isPlusMember],
    );

    const handleBuyBtn = () => {
        // Check if basket is initialized before proceeding
        if (!isInitialized) {
            return;
        }

        if (onBuyBtn && ctaButtonProductCard.translationKey) {
            const payload = {
                productId: id,
                orderable: cust_in_stock,
                quantity: 1,
                url,
            };

            setIsDisabled(true);

            onBuyBtn(listId, {
                type: ctaButtonProductCard.translationKey,
                productEventData,
                payload,
                quantity: 1,
            });

            setIsDisabled(false);
        }
    };

    const onProductClick = useCallback(() => {
        sessionStorage.setItem('itemClicked', JSON.stringify({ id }));
        setList(listId);

        if (listId === GTM_LIST_ID.PRODUCT_CAROUSEL || listId === raptorAlgorithm) {
            productClickEvent(listId, listName, productEventData, raptorAlgorithm);
        } else {
            const itemListName = breadcrumb ? getListName(breadcrumb) : '';
            productClickEvent(listId, itemListName, productEventData, raptorAlgorithm);
        }
    }, [id, listId, productEventData, raptorAlgorithm, breadcrumb]);

    // MICRO-OPTIMIZATION: We are memoizing this to keep the object reference consistent across re-renders.
    const swatchesImages = useMemo(() => cust_swatchimages_siblings, [cust_swatchimages_siblings]);

    return (
        <div
            data-id={id}
            data-testid={`product-${position}`}
            className={styles.productCardComponent}
        >
            <ProductCardLink url={url} onClick={onProductClick}>
                <AspectRatio className={styles.productAspectRation} ratio={1}>
                    {(labelsTopLeft.length >= 1 || labelsTopRight.length >= 1) && (
                        <TopInfo
                            left={labelsTopLeft as ProductLabelSetting[]}
                            right={labelsTopRight as ProductLabelSetting[]}
                        />
                    )}

                    {firstImage && (
                        <Image
                            {...firstImage}
                            layout="responsive"
                            loading={imageLoading}
                            priority={priority}
                            width={images[0].width || 450}
                            height={images[0].height || 450}
                            alt={firstImage.alt}
                            sizes={`${mq(0, 'md', false)} 50vw, ${mq(0, 'lg', false)} 34vw, 25vw`}
                            /**
                             * Only for debugging purposes.
                             * Should be set to 'contain' before golive.
                             */
                            objectFit="cover"
                        />
                    )}

                    {onFavourite && (
                        <Favourite
                            isFavourite={isFavourite}
                            onFavourite={() => onFavourite(product)}
                        />
                    )}

                    {swatchesImages && swatchesImages.length > 1 && (
                        <Swatches swatchesOptions={cust_swatchimages_siblings} />
                    )}
                </AspectRatio>

                <Flex flex style={{ position: 'relative' }}>
                    {splash &&
                        !!product.cust_promotionId &&
                        !!frame?.settings?.pageReferences.campaignPage?.url && (
                            <Splash variant={splash.variant}>{splash.text}</Splash>
                        )}

                    <div className={styles.productDetails}>
                        <Flex column>
                            {typeof product?.cust_yotpoAverageRating === 'number' &&
                                product.cust_yotpoAverageRating > 0 && (
                                    <Rating
                                        rating={product.cust_yotpoAverageRating}
                                        totalRatings={product.cust_yotpoReviewCount}
                                    />
                                )}

                            {brand && <p className={styles.productLabel}>{brand.text}</p>}

                            <p className={styles.productCardName} title={longName}>
                                {name}
                            </p>

                            <p className={styles.productDescription} title={short_description}>
                                {label || <br />}
                            </p>
                        </Flex>

                        <Flex column>
                            {isGiftCard ? (
                                <FromPrice
                                    price={cust_minimumPrice}
                                    label={translate('common.from' as TranslationKey)}
                                    priceType={PriceTypes.DEFAULT}
                                />
                            ) : (
                                <Prices prices={prices || []} />
                            )}

                            <div className={styles.productFooter}>
                                {!!notifications?.length && (
                                    <Notifications notifications={notifications} />
                                )}

                                {toBoolean(ctaButtonProductCard?.shouldRender) && (
                                    <CTA
                                        disabled={
                                            toBoolean(ctaButtonProductCard?.isDisabled) ||
                                            isDisabled ||
                                            !isInitialized
                                        }
                                        text={translate(
                                            `plp.${ctaButtonProductCard.translationKey}` as TranslationKey,
                                        )}
                                        onClick={handleBuyBtn}
                                    />
                                )}
                            </div>
                        </Flex>
                    </div>
                </Flex>
            </ProductCardLink>
        </div>
    );
};
